import React, { useState, useEffect } from 'react';
import { Book, BookFormData, BookStatus } from '../../types/Book';
import { generateId } from '../../utils/bookUtils';
import './BookForm.css';

interface BookFormProps {
  book?: Book;
  onSubmit: (book: Book) => void;
  onCancel: () => void;
}

const BookForm: React.FC<BookFormProps> = ({ book, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState<BookFormData>({
    title: '',
    author: '',
    isbn: '',
    genre: '',
    status: BookStatus.WANT_TO_READ,
    notes: '',
    coverUrl: '',
    coverImageFile: undefined,
    tags: [],
    currentPage: undefined,
    totalPages: undefined
  });
  
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');

  useEffect(() => {
    if (book) {
      setFormData({
        title: book.title,
        author: book.author,
        isbn: book.isbn || '',
        genre: book.genre,
        status: book.status,
        notes: book.notes || '',
        coverUrl: book.coverUrl || '',
        coverImageFile: undefined,
        tags: book.tags,
        currentPage: book.currentPage,
        totalPages: book.totalPages || book.pageCount
      });
      
      // Set preview URL if book has existing cover
      if (book.coverUrl) {
        setPreviewUrl(book.coverUrl);
      }
    }
  }, [book]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.author.trim()) {
      alert('Title and Author are required!');
      return;
    }

    if (isLoading) {
      return; // Prevent multiple submissions
    }

    setIsLoading(true);
    setLoadingMessage(book ? 'Updating book...' : 'Adding book...');

    try {
      const bookData: Book = {
        id: book?.id || generateId(),
        title: formData.title.trim(),
        author: formData.author.trim(),
        isbn: formData.isbn?.trim() || undefined,
        genre: formData.genre,
        status: formData.status,
        dateAdded: book?.dateAdded || new Date().toISOString(),
        dateRead: formData.status === BookStatus.READ ? new Date().toISOString() : book?.dateRead,
        notes: formData.notes?.trim() || undefined,
        coverUrl: formData.coverUrl?.trim() || undefined,
        coverImageFile: formData.coverImageFile,
        pageCount: formData.totalPages,
        tags: formData.tags,
        currentPage: formData.currentPage,
        totalPages: formData.totalPages
      };

      // Update loading message if there's a cover image to upload
      if (formData.coverImageFile) {
        setLoadingMessage(book ? 'Updating book and uploading cover...' : 'Adding book and uploading cover...');
      }

      await onSubmit(bookData);
    } catch (error) {
      console.error('Error submitting book:', error);
      alert('An error occurred while saving the book. Please try again.');
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'currentPage' || name === 'totalPages'
        ? (value ? parseInt(value) : undefined)
        : value
    }));
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      setFormData(prev => ({ ...prev, coverImageFile: file, coverUrl: '' }));
      
      // Show loading while processing file
      setIsLoading(true);
      setLoadingMessage('Processing image...');
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result as string);
        setIsLoading(false);
        setLoadingMessage('');
      };
      reader.onerror = () => {
        alert('Error reading file. Please try again.');
        setIsLoading(false);
        setLoadingMessage('');
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, coverImageFile: undefined, coverUrl: '' }));
    setPreviewUrl('');
    // Reset file input
    const fileInput = document.getElementById('coverFile') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{book ? 'Edit Book' : 'Add New Book'}</h2>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="book-form">
          {isLoading && (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
              <p className="loading-message">{loadingMessage}</p>
            </div>
          )}
          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              disabled={isLoading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="author">Author *</label>
            <input
              type="text"
              id="author"
              name="author"
              value={formData.author}
              onChange={handleChange}
              disabled={isLoading}
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="genre">Genre</label>
              <select
                id="genre"
                name="genre"
                value={formData.genre}
                onChange={handleChange}
                disabled={isLoading}
                required
              >
                <option value="">Select Genre</option>
                <option value="Fiction">Fiction</option>
                <option value="Non-Fiction">Non-Fiction</option>
                <option value="Mystery">Mystery</option>
                <option value="Romance">Romance</option>
                <option value="Science Fiction">Science Fiction</option>
                <option value="Fantasy">Fantasy</option>
                <option value="Biography">Biography</option>
                <option value="History">History</option>
                <option value="Self-Help">Self-Help</option>
                <option value="Business">Business</option>
                <option value="Technology">Technology</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={isLoading}
              >
                <option value={BookStatus.WANT_TO_READ}>Want to Read</option>
                <option value={BookStatus.CURRENTLY_READING}>Currently Reading</option>
                <option value={BookStatus.READ}>Read</option>
                <option value={BookStatus.DNF}>Did Not Finish</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="currentPage">Current Page</label>
              <input
                type="number"
                id="currentPage"
                name="currentPage"
                value={formData.currentPage || ''}
                onChange={handleChange}
                disabled={isLoading}
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="totalPages">Total Pages</label>
              <input
                type="number"
                id="totalPages"
                name="totalPages"
                value={formData.totalPages || ''}
                onChange={handleChange}
                disabled={isLoading}
                min="1"
              />
            </div>
          </div>

          {(formData.currentPage !== undefined && formData.currentPage > 0 && formData.totalPages && formData.totalPages > 0) && (
            <div className="form-group">
              <label>Reading Progress</label>
              <div style={{ padding: '0.5rem 0' }}>
                <div style={{ marginBottom: '0.25rem', fontSize: '0.875rem', color: '#666666' }}>
                  Page {formData.currentPage} / {formData.totalPages} ({Math.round((formData.currentPage / formData.totalPages) * 100)}%)
                </div>
                <div style={{ width: '100%', height: '4px', background: '#f0f0f0', borderRadius: '2px', overflow: 'hidden' }}>
                  <div 
                    style={{ 
                      height: '100%', 
                      background: '#333333', 
                      borderRadius: '2px', 
                      width: `${Math.min((formData.currentPage / formData.totalPages) * 100, 100)}%`,
                      transition: 'width 0.3s ease'
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="isbn">ISBN</label>
            <input
              type="text"
              id="isbn"
              name="isbn"
              value={formData.isbn}
              onChange={handleChange}
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label>Cover Image</label>
            <div className="cover-upload-section">
              {previewUrl && (
                <div className="cover-preview">
                  <img src={previewUrl} alt="Cover preview" className="cover-preview-image" />
                  <button type="button" className="remove-image-btn" onClick={handleRemoveImage}>
                    ×
                  </button>
                </div>
              )}
              
              <div className="cover-upload-options">
                <div className="upload-option">
                  <label 
                    htmlFor="coverFile" 
                    className={`file-upload-label ${isLoading ? 'disabled' : ''}`}
                  >
                    {previewUrl ? 'Change Image' : 'Upload Image'}
                  </label>
                  <input
                    type="file"
                    id="coverFile"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="file-upload-input"
                    disabled={isLoading}
                  />
                </div>
                
                <div className="upload-divider">or</div>
                
                <div className="upload-option">
                  <label htmlFor="coverUrl">Image URL</label>
                  <input
                    type="url"
                    id="coverUrl"
                    name="coverUrl"
                    value={formData.coverUrl}
                    onChange={handleChange}
                    placeholder="https://example.com/image.jpg"
                    disabled={!!formData.coverImageFile || isLoading}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              disabled={isLoading}
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="button-spinner"></span>
                  {book ? 'Updating...' : 'Adding...'}
                </>
              ) : (
                book ? 'Update Book' : 'Add Book'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookForm;